import React, { createContext, useContext, useState, useEffect } from 'react';

const CookieConsentContext = createContext();

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState({
    essential: true, // Always true - required for site functionality
    functional: false,
    analytics: false,
    marketing: false,
    preferences: false
  });
  
  const [showBanner, setShowBanner] = useState(false);
  const [showManagement, setShowManagement] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    const savedConsent = localStorage.getItem('cookieConsent');
    if (savedConsent) {
      try {
        const parsedConsent = JSON.parse(savedConsent);
        setConsent(parsedConsent);
        setShowBanner(false);
      } catch (error) {
        console.error('Error parsing saved cookie consent:', error);
        setShowBanner(true);
      }
    } else {
      setShowBanner(true);
    }
    setIsLoaded(true);
  }, []);

  // Save consent to localStorage whenever it changes
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem('cookieConsent', JSON.stringify(consent));
      
      // Update Google Analytics consent if analytics is enabled
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: consent.analytics ? 'granted' : 'denied',
          ad_storage: consent.marketing ? 'granted' : 'denied',
          functionality_storage: consent.functional ? 'granted' : 'denied',
          personalization_storage: consent.preferences ? 'granted' : 'denied'
        });
      }
    }
  }, [consent, isLoaded]);

  const acceptAll = () => {
    setConsent({
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
      preferences: true
    });
    setShowBanner(false);
    setShowManagement(false);
  };

  const acceptEssential = () => {
    setConsent({
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
      preferences: false
    });
    setShowBanner(false);
    setShowManagement(false);
  };

  const updateConsent = (newConsent) => {
    setConsent({
      ...consent,
      ...newConsent
    });
  };

  const openManagement = () => {
    setShowManagement(true);
    setShowBanner(false);
  };

  const closeManagement = () => {
    setShowManagement(false);
  };

  const resetConsent = () => {
    localStorage.removeItem('cookieConsent');
    setConsent({
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
      preferences: false
    });
    setShowBanner(true);
    setShowManagement(false);
  };

  // Development helper - add to window for easy testing
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.resetCookieConsent = resetConsent;
      window.showCookieBanner = () => setShowBanner(true);
      window.hideCookieBanner = () => setShowBanner(false);
    }
  }, []);

  const value = {
    consent,
    showBanner,
    showManagement,
    isLoaded,
    acceptAll,
    acceptEssential,
    updateConsent,
    openManagement,
    closeManagement,
    resetConsent
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};
